import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Clock, Package } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { Link } from 'react-router-dom';
import { formatDateDisplay } from '@/lib/utils';

interface CartSelectionProps {
  onClose?: () => void;
}

export function CartSelection({ onClose }: CartSelectionProps) {
  const { savedCarts, setActiveCart, createNewCart } = useCart();

  const handleSelectCart = async (secretId: string) => {
    await setActiveCart(secretId);
    onClose?.();
  };

  const handleCreateNew = async () => {
    await createNewCart();
    onClose?.();
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => formatDateDisplay(dateString);

  return (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <ShoppingCart className="h-8 w-8 text-muted-foreground mx-auto" />
        <h3 className="font-medium text-foreground">Select a Cart</h3>
        <p className="text-sm text-muted-foreground">
          You have multiple saved carts. Please select one to continue.
        </p>
      </div>

      {/* Recent Carts */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-foreground">Recent Carts</h4>
        {savedCarts.slice(0, 3).map((cart) => (
          <Card key={cart.id} className="border-border cursor-pointer hover:bg-accent/50 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm text-foreground truncate">
                    {cart.name || `Cart ${cart.id.slice(-6)}`}
                  </span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {cart.totalItems} items
                </Badge>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {formatDate(cart.lastModified)}
                </div>
                <span className="font-medium text-foreground">
                  {formatCurrency(cart.totalAmount, cart.currencyCode)}
                </span>
              </div>
              
              <Button
                onClick={() => handleSelectCart(cart.secretId)}
                className="w-full mt-3"
                size="sm"
              >
                Use This Cart
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions */}
      <div className="space-y-2 pt-4 border-t border-border">
        <Button
          onClick={handleCreateNew}
          variant="outline"
          className="w-full"
        >
          Create New Cart
        </Button>
        
        {savedCarts.length > 3 && (
          <Button
            variant="ghost"
            className="w-full"
            asChild
            onClick={onClose}
          >
            <Link to="/saved-carts">
              View All Saved Carts ({savedCarts.length})
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}